/**
 * Search & Filtering — Action definitions for molecular and fragment search
 *
 * Covers fragment name lookup, substructure search, spatial search,
 * general search engine queries, and low-level BMapsDS queries.
 *
 * @module gibbs/registry/actions/search-filter
 */

import { WorkflowTypes as T } from '../types';
import { param, pre } from '../schema';

// ─── Fragment Search ───────────────────────────────────────────────────────

export const searchFragmentsByName = {
    id: 'search-fragments-by-name',
    name: 'Fragment Name Lookup',
    category: 'Search & Filtering',
    description: 'Batch lookup of fragments by name across all libraries. Returns fragment metadata and molecular properties.',
    inputs: [
        param('names', 'string[]', 'Fragment names to look up'),
    ],
    outputs: [
        param('results', 'object[]', 'Matching fragments with properties per library'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('fragment_data_available', 'Fragment libraries must be available'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: null,
    serverCommand: 'lookupByName',
    jobType: null,
};

export const substructureSearch = {
    id: 'substructure-search',
    name: 'Substructure Search',
    category: 'Search & Filtering',
    description: 'Search fragment libraries for compounds containing a specified substructure using fingerprint-based matching. Supports pagination.',
    inputs: [
        param('query', T.SMILES, 'Substructure pattern to search for (SMILES or SMARTS)'),
    ],
    outputs: [
        param('results', 'object[]', 'Matching fragment ordinals'),
    ],
    parameters: [
        param('start', 'number', 'Pagination offset', false, { defaultValue: 0 }),
        param('count', 'number', 'Page size', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('fragment_data_available', 'Fragment library must be indexed'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: null,
    serverCommand: 'substructureSearch',
    jobType: null,
};

export const spatialFragmentSearch = {
    id: 'spatial-fragment-search',
    name: 'Spatial Fragment Search (3D)',
    category: 'Search & Filtering',
    description: 'Search for fragments within a 3D radius of a point in the protein structure. Uses octree spatial indexing for efficient geometric queries. Supports both proximity (FindNear) and directional (FindDirectional) searches.',
    inputs: [
        param('center', 'object', 'Search center XYZ coordinates'),
        param('radius', 'number', 'Search radius in Angstroms'),
        param('direction', 'object', 'Directional vector for cone search', false),
    ],
    outputs: [
        param('results', T.FragmentSearchResult, 'Matching fragment postures within radius'),
    ],
    parameters: [
        param('searchType', 'string', 'Type of spatial search', false, { options: ['FindNear', 'FindDirectional'] }),
        param('rmin', 'number', 'Min radius for directional search', false),
        param('rmax', 'number', 'Max radius for directional search', false),
        param('dtheta', 'number', 'Angle threshold for directional cone', false),
        param('caseExtras', 'object', 'Per-case coordinate transforms', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('fragment_data_available', 'Fragment data must be indexed (BMapsDS)'),
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'FragDataQuery',
    serverCommand: 'frag-data-query',
    jobType: null,
};

export const searchEngineQuery = {
    id: 'search-engine-query',
    name: 'Search Engine Query',
    category: 'Search & Filtering',
    description: 'Execute a molecular search query against the BMaps search service. Supports property-based, name-based, and filtered searches across fragment libraries.',
    inputs: [
        param('query', 'object', 'Search request specification'),
    ],
    outputs: [
        param('results', 'object[]', 'Search hits with properties'),
    ],
    parameters: [
        param('librarySelection', 'string', 'Library to search', false),
        param('propertyFilters', 'object', 'Property-based filters', false),
        param('sortCriteria', 'string', 'Sort order for results', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('internet_available', 'Search service must be reachable'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: null,
    serverCommand: 'searchEngine',
    jobType: null,
};

export const bmapsdsQuery = {
    id: 'bmapsds-query',
    name: 'BMapsDS Data Query',
    category: 'Search & Filtering',
    description: 'Low-level query against the BMaps Data Service query engine. Three-tier filtering: filesystem (dataSource, project, case) -> database (fragment, B-value, seed) -> per-posture (geometric). Supports streaming results.',
    inputs: [
        param('filterSeries', 'object[]', 'Array of filter specifications [field, comparison, value]'),
        param('caseExtras', 'object', 'Per-case metadata and transforms', false),
    ],
    outputs: [
        param('results', 'object', 'Fragment posture data (binary or text format)'),
    ],
    parameters: [
        param('outputFormat', 'string', 'Output format', false, { defaultValue: 'binary', options: ['binary', 'binary-legacy', 'text'] }),
    ],
    dependsOn: [],
    preconditions: [
        pre('fragment_data_available', 'BMapsDS service must be running'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: null,
    serverCommand: 'frag-data-query',
    jobType: null,
};

// ─── Default export: all actions in this category ──────────────────────────

export default [
    searchFragmentsByName,
    substructureSearch,
    spatialFragmentSearch,
    searchEngineQuery,
    bmapsdsQuery,
];
